"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, ArrowRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const guideTopics = [{
  title: "Intended Usage",
  detail: "Permanent venues vs. mobile entertainment operators"
}, {
  title: "Machine Condition",
  detail: "Comparing new factory equipment against used inventory"
}, {
  title: "Controller & Interface",
  detail: "Console features, manual speeds, and safety controls"
}, {
  title: "Inflatable Safety Arena",
  detail: "Mattress footprint, blower setup, and containment styles"
}, {
  title: "Power & Space Needs",
  detail: "Electrical circuit readiness and required floor clearance"
}, {
  title: "Pricing & Inclusions",
  detail: "Evaluating total purchase price and what comes included"
}, {
  title: "Replacement Parts",
  detail: "Long-term access to wear components and maintenance items"
}, {
  title: "Delivery & Logistics",
  detail: "Freight arrangements, offloading, and site delivery"
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative bg-muted text-foreground border-b border-border py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Main Content Column */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Spec Plate Eyebrow */}
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 border border-border bg-background px-3 py-1.5 mb-6">
              <BookOpen className="size-4 text-primary" />
              <span className="font-mono text-xs md:text-sm font-medium tracking-wider text-muted-foreground uppercase">
                MECHANICAL BULL BUYING GUIDE
              </span>
            </motion.div>

            {/* Exact H1 Title */}
            <motion.h1 variants={itemVariants} className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-foreground mb-6">
              How to Choose a Mechanical Bull
            </motion.h1>

            {/* Body Copy Paragraph 1 */}
            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-4">
              Choosing a mechanical bull starts with how you plan to use it. A machine for a permanent entertainment venue can create different priorities from equipment that travels between events, while a buyer comparing new and used bulls needs to look closely at condition, included equipment and future parts availability.
            </motion.p>

            {/* Body Copy Paragraph 2 */}
            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
              This guide walks through the decisions that matter before you buy a mechanical bull, including the machine itself, controller, inflatable safety arena, power and space requirements, pricing, replacement parts and delivery.
            </motion.p>

            {/* Primary Action Row */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 font-serif uppercase tracking-wider text-base font-semibold border border-primary transition-[color,background-color,border-color]">
                <Link to="/NewMechanicalBullsPage">
                  Browse New Bulls
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-12 px-6 font-serif uppercase tracking-wider text-base font-semibold border-foreground text-foreground hover:bg-foreground hover:text-background transition-[color,background-color,border-color]">
                <Link to="/UsedMechanicalBullsPage">
                  View Used Bulls
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Guide Scope Visual Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5 bg-card border border-border p-6 sm:p-8">
            <div className="pb-4 border-b border-border mb-6">
              <span className="font-mono text-xs text-primary uppercase tracking-wider font-semibold block mb-1">
                Buyer Overview
              </span>
              <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wider text-foreground">
                What This Guide Covers
              </h2>
            </div>

            <p className="font-sans text-sm text-muted-foreground mb-6">
              A comprehensive walkthrough of every essential factor to assess before purchasing commercial mechanical bull equipment:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {guideTopics.map((topic, i) => <div key={topic.title} data-index={i} className="p-3 bg-muted border border-border">
                  <div className="flex items-start gap-2">
                    <Check className="size-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-serif text-sm font-semibold uppercase tracking-wider text-foreground">
                        {topic.title}
                      </h3>
                      <p className="font-sans text-xs text-muted-foreground leading-normal mt-0.5">
                        {topic.detail}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>

            <div className="mt-6 pt-4 border-t border-border">
              <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                Review each section below to compare options, understand equipment packages, and plan your operating requirements.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}